import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead, Panel, F, In, Area, Toggle, Btn, Sel } from "@/components/admin/kit";
import { NotificationList } from "@/components/admin/Lists";
import { Loading } from "@/components/site/Primitives";

const TemplateEditor = ({ t, canEdit, onSaved }) => {
  const [f, setF] = useState({ subject: t.subject, heading: t.heading, body: t.body, enabled: t.enabled });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    try { await api.put(`/admin/email-templates/${t.key}`, f); toast.success("Template saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title={t.label} testid={`template-${t.key}`}><div className="space-y-3">
      <F label="Subject"><In value={f.subject} onChange={set("subject")} testid={`template-${t.key}-subject`} /></F>
      <F label="Heading"><In value={f.heading} onChange={set("heading")} testid={`template-${t.key}-heading`} /></F>
      <F label="Body" hint="Use {{placeholders}} as in the default text. Blank lines create paragraphs."><Area value={f.body} onChange={set("body")} rows={6} testid={`template-${t.key}-body`} /></F>
      <div className="flex items-center justify-between">
        <Toggle label="Enabled" checked={f.enabled} onChange={set("enabled")} testid={`template-${t.key}-enabled`} />
        {canEdit && <Btn onClick={save} data-testid={`template-${t.key}-save`}>Save</Btn>}
      </div>
    </div></Panel>
  );
};

export default function EmailsAdmin() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState(null);
  const [log, setLog] = useState(null);
  const [status, setStatus] = useState("");
  const loadT = useCallback(() => api.get("/admin/email-templates").then((r) => setTemplates(r.data)), []);
  const loadLog = useCallback(() => api.get("/admin/notifications", { params: { status } }).then((r) => setLog(r.data)), [status]);
  useEffect(() => {
    loadT();
  }, [loadT]);
  useEffect(() => {
    loadLog();
  }, [loadLog]);
  const retry = async (id) => {
    try { const r = await api.post(`/admin/notifications/${id}/retry`); toast[r.data.status === "sent" ? "success" : "error"](`Retry: ${r.data.status}${r.data.error ? ` (${r.data.error})` : ""}`); loadLog(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-emails">
      <PageHead eyebrow="System" title="Emails & notifications" />
      <Tabs defaultValue="log">
        <TabsList className="bg-transparent border-b border-[#27272A] w-full justify-start rounded-none h-auto p-0 mb-6">
          <TabsTrigger value="log" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0055FF] data-[state=active]:bg-transparent px-4 py-3" data-testid="emails-tab-log">Delivery log</TabsTrigger>
          <TabsTrigger value="templates" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#0055FF] data-[state=active]:bg-transparent px-4 py-3" data-testid="emails-tab-templates">Templates</TabsTrigger>
        </TabsList>
        <TabsContent value="log">
          <div className="w-60 mb-4"><Sel value={status} onChange={setStatus} options={["sent", "failed", "skipped"]} placeholder="All statuses" testid="emails-filter-status" /></div>
          <Panel>{log ? <NotificationList items={log} onRetry={retry} /> : <Loading rows={1} />}</Panel>
        </TabsContent>
        <TabsContent value="templates">
          {!templates ? <Loading rows={2} /> : <div className="grid xl:grid-cols-2 gap-6">{templates.map((t) => <TemplateEditor key={t.key} t={t} canEdit={user?.role === "admin"} onSaved={loadT} />)}</div>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
